import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Sparkles, ArrowRight } from "lucide-react";
import { StatusBadge, EASE } from "./shared";
import { TRAVEL_TIMES, TRAVEL_ORIGIN, NIA_TRANSPORT } from "../data/site";

export function TravelTimeEstimator({ onSelectMode }) {
  const [dest, setDest] = useState(TRAVEL_TIMES[0].id);
  const row = TRAVEL_TIMES.find((d) => d.id === dest);
  const link = NIA_TRANSPORT.find((m) => m.id === row.future.enabledBy);

  return (
    <div className="border border-[#D4AF37]/15 bg-[#0A0A0A] rounded-[2px] p-6 lg:p-8" data-testid="travel-time-estimator">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Travel Time Estimator</p>
          <p className="font-serif-lux text-2xl text-[#F5F5F0]">From GYC to&hellip;</p>
        </div>
        <p className="text-[11px] text-[#71717A]">Origin: {TRAVEL_ORIGIN}</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="travel-destinations">
        {TRAVEL_TIMES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDest(d.id)}
            aria-pressed={dest === d.id}
            data-testid={`travel-dest-${d.id}`}
            className={`px-3.5 py-1.5 text-[10px] uppercase tracking-[0.16em] rounded-full border transition-colors ${
              dest === d.id ? "bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black border-transparent" : "border-[#D4AF37]/20 text-[#71717A] hover:text-[#F3E5AB]"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={row.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.4, ease: EASE }}>
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-4 items-stretch">
            <div className="card-lux p-5 rounded-[2px]" data-testid="travel-today">
              <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3"><Car size={13} /> Today</p>
              <p className="font-serif-lux text-3xl text-[#F5F5F0]" data-testid="travel-today-time">{row.today.time}</p>
              <p className="text-[12px] text-[#A1A1AA] mt-2">{row.today.mode}</p>
            </div>
            <div className="hidden sm:grid place-items-center text-[#D4AF37]"><ArrowRight size={18} /></div>
            <div className="p-5 rounded-[2px] border border-[#D4AF37]/50 bg-[#D4AF37]/[0.06]" data-testid="travel-future">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]"><Sparkles size={13} /> Future</p>
                <StatusBadge status={row.future.status} />
              </div>
              <p className="font-serif-lux text-3xl text-gold-gradient" data-testid="travel-future-time">{row.future.time}</p>
              <p className="text-[12px] text-[#F5F5F0]/85 mt-2">{row.future.mode}</p>
              <button
                type="button"
                onClick={() => onSelectMode(link.id)}
                data-testid="travel-enabled-by-btn"
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#F3E5AB] hover:text-[#D4AF37] transition-colors"
              >
                Enabled by: {link.mode} <ArrowRight size={11} />
              </button>
            </div>
          </div>
          <p className="mt-4 text-[12px] text-[#A1A1AA] leading-relaxed" data-testid="travel-note">{row.note}</p>
        </motion.div>
      </AnimatePresence>

      <p className="mt-4 text-[11px] text-[#71717A] leading-relaxed" data-testid="travel-disclaimer">
        Indicative ranges only, compiled from public project reports and typical off-peak driving conditions. Future
        times assume each link opens as currently proposed and exclude waiting, interchange and first/last-mile time.
        Not a guarantee - verify with the respective authority.
      </p>
    </div>
  );
}
